import { FormEvent, useState, useTransition } from "react";
import { toast } from "sonner";

import { newPasswordIssue, PASSWORD_MAX, PASSWORD_MIN } from "@buildpy/core/modules/org/password";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import { setSignupPassword, signupStatus } from "@/api/actions/onboarding";
import { navigateTo } from "@/lib/nav";
import { getSupabase } from "@/lib/supabase";
import { finishReset, loginAfterResetHref, saveResetPassword } from "./reset-form-state";

interface ResetPasswordFormProps {
  onExpired?: () => void;
  // 저장을 바꿔 끼운다(가입 비밀번호 정하기) — 주면 Supabase updateUser·세션 끊기 대신 이것만 부른다.
  onSave?: (password: string, confirm: string) => Promise<void>;
  submitLabel?: string;
  pendingLabel?: string;
}

// 새 비밀번호 두 칸 — 규칙은 초대 수락과 같은 한 벌 · 바꾸면 모든 기기 세션을 끊고 로그인 화면으로.
const ResetPasswordForm = ({ onExpired, onSave, submitLabel = "비밀번호 바꾸기", pendingLabel = "바꾸는 중…" }: ResetPasswordFormProps) => {
  const [pending, startTransition] = useTransition();
  const [issue, setIssue] = useState<string | null>(null);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    const confirm = String(form.get("confirm") ?? "");
    const problem = newPasswordIssue(password, confirm);
    setIssue(problem);
    if (problem) return;

    startTransition(async () => {
      if (onSave) {
        await onSave(password, confirm);
        return;
      }
      const auth = getSupabase().auth;
      const outcome = await saveResetPassword(password, confirm, {
        status: signupStatus,
        setSignupPassword,
        updateUser: (next) => auth.updateUser({ password: next }),
      });
      if (outcome.kind === "expired") {
        onExpired?.();
        return;
      }
      if (outcome.kind === "failed") {
        toast.error(outcome.message);
        return;
      }
      await finishReset(auth, (everywhere) => navigateTo(loginAfterResetHref(everywhere), true));
    });
  };

  return (
    <form className="flex flex-col gap-y-4" noValidate onSubmit={onSubmit}>
      <div className="flex flex-col gap-y-1.5">
        <Label htmlFor="reset-password">새 비밀번호</Label>
        <Input
          autoComplete="new-password"
          disabled={pending}
          id="reset-password"
          maxLength={PASSWORD_MAX}
          minLength={PASSWORD_MIN}
          name="password"
          required
          type="password"
        />
        <p className="text-xs text-muted-foreground">{PASSWORD_MIN}자 이상</p>
      </div>

      <div className="flex flex-col gap-y-1.5">
        <Label htmlFor="reset-confirm">새 비밀번호 확인</Label>
        <Input
          autoComplete="new-password"
          disabled={pending}
          id="reset-confirm"
          maxLength={PASSWORD_MAX}
          name="confirm"
          required
          type="password"
        />
      </div>

      {issue && (
        <p className="text-sm text-destructive" role="alert">
          {issue}
        </p>
      )}

      <Button className="w-full" disabled={pending} type="submit">
        {pending ? pendingLabel : submitLabel}
      </Button>
    </form>
  );
};

export default ResetPasswordForm;
